/**
 * BESTIX v5.0 — Hero Section
 * Photography-first hero with frosted glass search
 */

import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UnifiedSearch } from '@/components/search/UnifiedSearch';

// ============================================
// Types
// ============================================

interface HeroProps {
  title: string;
  subtitle: string;
  backgroundImage: string;
  searchPlaceholder?: string;
  searchLabel?: string;
  showScrollIndicator?: boolean;
  height?: 'full' | 'large' | 'medium';
}

// ============================================
// Hero Component
// ============================================

export function Hero({
  title,
  subtitle,
  backgroundImage,
  searchPlaceholder = 'Search events, artists, venues...',
  searchLabel = 'WHERE WOULD YOU LIKE TO GO?',
  showScrollIndicator = true,
  height = 'full',
}: HeroProps) {
  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
  };

  return (
    <section className={cn('relative flex items-center justify-center', heightClasses[height])}>
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
        {/* Decorative Line */}
        <div className="w-16 h-px bg-white/60 mx-auto mb-8" />

        {/* Title */}
        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-white !text-white mb-2">
          {title}
        </h1>
        <p className="font-playfair italic text-4xl md:text-5xl lg:text-6xl text-white !text-white mb-8">
          {subtitle}
        </p>

        {/* Decorative Line */}
        <div className="w-16 h-px bg-white/60 mx-auto mb-12" />

        {/* Unified Search */}
        <UnifiedSearch
          placeholder={searchPlaceholder}
          label={searchLabel}
          variant="hero"
        />

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <button
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-pulse"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        )}
      </div>
    </section>
  );
}

// ============================================
// Category Hero Component
// ============================================

interface CategoryHeroProps {
  title: string;
  eventCount?: number;
  backgroundImage: string;
  headline?: string;
  subheadline?: string;
}

export function CategoryHero({
  title,
  eventCount,
  backgroundImage,
  headline,
  subheadline,
}: CategoryHeroProps) {
  const showFullHero = headline && subheadline;

  return (
    <section className={cn(
      'relative flex items-center justify-center',
      showFullHero ? 'min-h-[50vh]' : 'min-h-[30vh]'
    )}>
      <div className="absolute inset-0">
        <img src={backgroundImage} alt="" className="w-full h-full object-cover" />
        <div className="hero-overlay" />
      </div>

      <div className="relative z-10 text-center px-6 py-12">
        {showFullHero ? (
          <>
            <div className="w-12 h-px bg-white/60 mx-auto mb-6" />
            <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white !text-white mb-1">{headline}</h1>
            <p className="font-playfair italic text-3xl md:text-4xl lg:text-5xl text-white !text-white mb-6">{subheadline}</p>
            <div className="w-12 h-px bg-white/60 mx-auto" />
          </>
        ) : (
          <>
            <h1 className="font-playfair italic text-4xl md:text-5xl text-white !text-white mb-2">{title}</h1>
            {eventCount !== undefined && (
              <p className="text-white/70 text-lg">{eventCount} {eventCount === 1 ? 'event' : 'events'} found</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default Hero;
